/**
 * Product Lineup Section Component for Brand Portfolio Display
 * 
 * A comprehensive showcase component that presents the complete WoodAlps
 * product lineup with individual bike models, descriptions, and navigation.
 * Features flexible layout options and CMS integration.
 * 
 * Product Models Featured:
 * - GROOTY: Road bike with superhero tree transformation story
 * - BRANCHY: Gravel bike designed for adventure and exploration  
 * - YGGY: MTB model for mountain and forest environments
 * 
 * Content Management:
 * - Dynamic content from Sanity CMS with fallback defaults
 * - Rich text description support via Portable Text
 * - Flexible image management with CMS integration
 * - Category-specific routing and navigation
 * 
 * Layout Flexibility:
 * - Configurable title sizing (normal/small)
 * - Optional flowchart display for process visualization
 * - Adjustable top margin for section spacing
 * - Customizable background color options
 * - Responsive grid layout for product presentation
 * 
 * Visual Features:
 * - Professional product photography display
 * - Engaging product descriptions and storytelling
 * - Clear category identification and navigation
 * - Consistent brand messaging and visual hierarchy
 * - Responsive image handling and optimization
 * 
 * Brand Storytelling:
 * - Each bike model has unique personality and narrative
 * - Connection to nature and environmental themes
 * - Adventure and lifestyle positioning
 * - Emotional engagement through character development
 * 
 * Navigation Integration:
 * - Direct links to category pages (Road, Gravel, MTB)
 * - Seamless user flow to detailed product information
 * - Clear call-to-action elements for exploration
 * - Category-specific routing and deep linking
 * 
 * Responsive Design:
 * - Mobile-optimized layout and interactions
 * - Adaptive image sizing and aspect ratios
 * - Responsive typography and spacing
 * - Touch-friendly navigation elements
 * 
 * Performance:
 * - Efficient content loading with fallback systems
 * - Optimized image delivery via Next.js
 * - Component composition for reusability
 * - Minimal re-renders with stable references
 * 
 * @param {object} data - Product lineup data from Sanity CMS
 * @param {boolean} smallTitle - Whether to use smaller title styling
 * @param {boolean} showFlowchart - Whether to display process flowchart
 * @param {boolean} reducedTopMargin - Whether to reduce top spacing
 * @param {string} bgColor - Custom background color for section
 * 
 * @description Complete product lineup showcase with storytelling and flexible layout options
 */

import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '../../src/hooks/useTranslation';
import React from 'react';
import { } from '../../src/app/studio/lib/sanity';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import PortableText from '../../src/components/sanity/PortableText';

const ProductLineupSection = ({ data, smallTitle = false, showFlowchart = true, reducedTopMargin = false, bgColor = '' }) => {
  const { t } = useTranslation();
  // Get the top padding class based on the prop
  const topPaddingClass = reducedTopMargin ? 'pt-2 sm:pt-4' : 'pt-16';

  const defaultBikes = [
    {
      id: 'grooty',
      name: 'GROOTY',
      category: 'Road',
      description:
        'Grooty was born from the transformation of a superhero tree, guardian of our planet Earth, into a character with the appearance of a plant-inspired bike, featuring organic shapes a clever blend of design and performance.',
      image: '/images/ROADPL.jpg',
      link: '/road'
    },
    {
      id: 'branchy',
      name: 'BRANCHY',
      category: 'Gravel',
      description:
        'The Branchy is designed for adventure, to explore new horizons in harmony with nature. Its unique design, organic shapes, and dynamic curves won\'t leave anyone indifferent — not even a tree along the edge of your path.',
      image: '/images/GRAVELPL.jpeg',
        link: '/gravel'
    },
    {
      id: 'yggy',
      name: 'YGGY',
      category: 'MTB',
      description:
        'The YGGY model feels at home in its natural environment: the mountains and the forest. It lives, it inspires, and makes you want to ride through winding trails in the heart of the forest and mountains from which it was born.',
      image: '/images/VTTPL.jpg',
      link: '/mtb'
    },
  ];

  // Get the individual product data or use defaults
  const getProductData = () => {
    let products = [];
    
    // Add GROOTY if available, otherwise use default
    if (data?.grooty) {
      products.push({
        id: 'grooty',
        name: data.grooty.name || 'GROOTY',
        category: data.grooty.category || 'Road',
        description: data.grooty.description || defaultBikes[0].description,
        image: data.grooty.image || '/images/ROADPL.jpg',
        link: '/road' // Always use road page for Grooty
      });
    } else {
      products.push(defaultBikes[0]);
    }
    
    // Add BRANCHY if available, otherwise use default
    if (data?.branchy) {
      products.push({
        id: 'branchy',
        name: data.branchy.name || 'BRANCHY',
        category: data.branchy.category || 'Gravel',
        description: data.branchy.description || defaultBikes[1].description,
        image: data.branchy.image || '/images/GRAVELPL.jpeg',
        link: '/gravel' // Always use gravel page for Branchy
      });
    } else {
      products.push(defaultBikes[1]);
    }

    // Add YGGY if available, otherwise use default
    if (data?.yggy) {
      products.push({
        id: 'yggy',
        name: data.yggy.name || 'YGGY',
        category: data.yggy.category || 'VTT',
        description: data.yggy.description || defaultBikes[2].description,
        image: data.yggy.image || '/images/VTTPL.jpg',
        link: '/mtb' // Always use mtb page for Yggy
      });
    } else {
      products.push(defaultBikes[2]);
    }
    
    // Add any additional products from the products array
    if (data?.products && data.products.length > 0) {
      const additionalProducts = data.products.map(product => {
        // Determine the correct link based on category
        let categoryLink = '#';
        const category = product.category?.toLowerCase() || '';
        
        if (category.includes('road')) {
          categoryLink = '/road';
        } else if (category.includes('gravel')) {
          categoryLink = '/gravel';
        } else if (category.includes('mtb') || category.includes('vtt')) {
          categoryLink = '/mtb';
        }
        
        return {
          id: product.name?.toLowerCase().replace(/\s+/g, '-') || 'product',
          name: product.name || 'Product',
          category: product.category || 'Bike',
          description: product.description || 'A beautiful wooden bike by WOODALPS.',
          image: product.image || '/images/VTTPL.jpg',
          link: categoryLink
        };
      });
      
      products = [...products, ...additionalProducts];
    }

    
    
    return products;
  };

  // Use data from Sanity if available, otherwise use default data
  const bikes = getProductData();

  // Function to render description with styled bike name
  const renderDescription = (description, bikeName) => {
    // Check if description is a portable text array
    if (Array.isArray(description)) {
      // For portable text, we return the PortableText component
      return <PortableText value={description} />;
    }
    
    // If it's a string, process it with the regex styling
    if (typeof description === 'string') {
      // Create a regular expression that matches the bike name, case-insensitive
      const regex = new RegExp(`(${bikeName})`, 'gi');
      
      // Split the description by the regex and keep the matches
      const parts = description.split(regex);
      
      // Return the parts with styled bike name in between
      return parts.map((part, index) => {
        // Check if this part matches the bike name (case insensitive)
        if (part.toLowerCase() === bikeName.toLowerCase()) {
          return <span key={index} className="font-neuropol">{part}</span>;
        }
        // Otherwise it's regular text
        return part;
      });
    }
    
    // Fallback for any other type
    return description?.toString() || '';
  };

  // Check if the description is an array (portable text) or not
  const isPortableText = (description) => {
    return Array.isArray(description);
  };

  // Helper function to safely get an image URL
  const getImageUrl = (image) => {
    if (image?.asset) {
      const url = urlForImage(image);
      return url || null;
    }
    return image; // Return the string path if it's a direct URL
  };

  return (
    <section className={`section ${topPaddingClass} ${bgColor}`}>
      <div className="container-custom">
        <div className="text-center mb-16">
          
          {/* Added flowchart image */}
          {showFlowchart && (
            <div className="max-w-4xl mx-auto mb-10">
              <Image
                src="/images/flowchartHOME.jpg"
                alt="Wooden Bike Lifecycle"
                width={900}
                height={500}
                sizes="(max-width: 768px) 100vw, 900px"
                className="mx-auto"
              />
            </div>
          )}
          {smallTitle ? (
            <h2 className="text-xl md:text-xl font-bold mb-6 text-black font-neuropol">{data?.title || 'Our Bikes'}</h2>
          ) : (
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black font-neuropol">{data?.title || 'Our Bikes'}</h2>
          )}
          <div className="w-20 h-1 bg-accent-color mx-auto mb-8"></div>
          {data?.subtitle && <p className="text-lg text-gray-600">{data.subtitle}</p>}
          
        </div>
  
        {/* Bike cards in a single row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:px-8 mb-4">
          {bikes.map((bike, index) => {
            // Safely get the image URL
            const imageUrl = getImageUrl(bike.image);
            
            return (
              <div key={bike.id || index} className="bg-gray-200 border border-gray-300 rounded-lg overflow-hidden hover:shadow-xl hover:border-accent-color hover:scale-[1.02] transition-all duration-300 transform flex flex-col">
                {/* Image */}
                <div className="relative aspect-[16/9] overflow-hidden group flex-shrink-0">
                  {imageUrl && (
                    <Image
                      src={imageUrl}
                      alt={bike.image?.alt || bike.name}
                      fill
                      loader={sanityLoader}
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-300"></div>
                </div>
  
                {/* Content */}
                <div className="p-6 flex flex-col h-full min-h-[320px]">
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold mb-1 text-gray-800 group-hover:text-accent-color transition-colors duration-300 font-neuropol">{bike.name}</h3>
                    <p className="text-accent-color mb-4 font-neuropol">{bike.category}</p>
                    
                    {/* Conditionally render based on description type */}
                    {isPortableText(bike.description) ? (
                      <div className="text-gray-600 mb-6 leading-relaxed text-justify">
                        {renderDescription(bike.description, bike.name)}
                      </div>
                    ) : (
                      <p className="text-gray-600 mb-6 leading-relaxed text-justify">
                        {renderDescription(bike.description, bike.name)}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-auto pt-4 flex justify-center">
                    <Link href={bike.link} 
                      className="inline-block px-6 py-2 bg-transparent text-gray-800 font-medium rounded-md border border-gray-800 hover:bg-accent-color hover:text-white hover:border-accent-color transition-all font-neuropol">
                      {t('buttons.learn_more')}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductLineupSection;
  